import { lazy, Suspense, type ReactNode } from 'react';
import Skeleton from '@mui/material/Skeleton';
import { RequirePermission } from '../permissions/RequirePermission';
import type { ExportButtonProps } from './ExportButton';

/**
 * The export button and the jobs tray (SDD-020 §9) as shared chunks, fetched on demand — the
 * PropertyFilter's reasoning (../data/lazy-lists.tsx): every list and detail page of every module
 * renders the button and the shell renders the tray, so neither belongs to a module's budget.
 * Loaded statically, Rolldown hoists them into `module-billing` with the rest of the shared
 * workspace code, and that chunk was at 399.8 of 400 kB before prompt 28 (measured with them
 * static: 400.9).
 *
 * The permission is checked BEFORE the boundary: a persona without the export key downloads
 * nothing. The screens import these wrappers from `@eis/patterns`; the stories import the
 * patterns themselves.
 */
const LazyExportButton = lazy(async () => ({
  default: (await import('./ExportButton')).ExportButton,
}));

const LazyJobsTray = lazy(async () => ({
  default: (await import('./JobsTray')).JobsTray,
}));

export function ExportButton(props: ExportButtonProps): ReactNode {
  return (
    <RequirePermission anyOf={[props.permission]}>
      <Suspense fallback={<Skeleton variant="rounded" width={120} height={30} />}>
        <LazyExportButton {...props} />
      </Suspense>
    </RequirePermission>
  );
}

export function JobsTray(): ReactNode {
  return (
    <Suspense fallback={<Skeleton variant="circular" width={32} height={32} />}>
      <LazyJobsTray />
    </Suspense>
  );
}
